/**
 * Help copy for Sandbox customization fields (shown via in-game ? tip).
 * Keys are the param name (last segment of `mode.foo` / `common.foo`).
 */

const HELP_BY_KEY: Record<string, string> = {
    // Shared / common
    ballsSize: "Radius of obstacle balls. Larger balls are easier to hit and harder to dodge.",
    ballsSpeed: "How fast balls move. Higher values make the run harder immediately.",
    ballSpeed: "How fast balls move. Higher values make the run harder immediately.",
    initialBalls: "How many balls exist when the run starts.",
    ballsPerSpawn: "How many balls are added each time a spawn wave triggers.",
    pointsPerSpawn: "Score needed between spawn waves. Lower = balls pile on sooner.",
    cursorSize: "Your cursor hit radius. Larger = easier intercepts, harder to slip through gaps.",
    cursorSpeed: "How quickly keyboard / WASD movement accelerates the cursor.",
    smoothCursor: "Pointer follow smoothing. 0 = instant; higher = softer lag behind the mouse.",
    spawnClearance: "Safe radius around the cursor where new balls cannot spawn. Higher = larger safe circle.",
    ballCollisionsEnabled: "When ON, balls bounce off each other instead of overlapping freely.",
    bounceSpeedBoost: "Extra speed multiplier applied on wall (or ball) bounces. 0 turns this overlay boost off.",
    bounceBoost: "Speed multiplier applied when a ball bounces. Higher = balls get angrier after walls.",
    bounceMult: "Roulette bounce speed multiplier after wall hits.",
    wasdEnabled: "Use WASD / D-Pad momentum movement instead of (or with) pointer steering.",
    progressionEnabled: "Adds the green Progression safe-lane rule from Progression mode.",
    magnetEnabled: "Balls gently drift toward your cursor.",

    // Orbit lives + motion
    coreRadiusFrac: "Size of the hollow core relative to the shorter screen edge.",
    cursorGapRef: "Extra gap between the core rim and your orbiting cursor.",
    angularAccel: "How quickly the cursor ramps up to max orbit speed when you hold a direction.",
    maxAngularSpeed: "Top orbit spin rate (radians/sec). Caps hold, keys, and slide steering.",
    angularDrag: "How quickly the cursor slows when you release input.",
    ballSpeedBase: "Inbound ball speed at score 0 (start of difficulty tier 0).",
    ballSpeedPerTier: "Extra ball speed gained for each full score tier.",
    spawnRateBase: "Balls spawned per second at score 0.",
    spawnRatePerTier: "Extra spawn rate gained for each full score tier.",
    livesStart: "Lives at run start. In Orbit these are the mini-cursors inside the core.",
    livesCap: "Maximum core lives. Score bonuses cannot push you above this cap.",
    lifeBonusEvery: "Score interval for a life bonus: restores a lost life first, else adds a slot up to Lives Cap.",
    livesMax: "Maximum lives for this mode (Combo).",

    // Mode-specific
    levelSpeedStep: "Speed multiplier applied each Progression level-up.",
    maxBalls: "Hard cap on how many balls Progression can stack.",
    tiltSensitivity: "How strongly device tilt moves the cursor.",
    shieldCooldownMs: "Cooldown before Superdot Shield (E) can be used again.",
    slowCooldownMs: "Cooldown before Superdot Slow (Q) can be used again.",
    barrierCooldownMs: "Cooldown before Superdot Barrier (W) can be used again.",
    pushCooldownMs: "Cooldown before Superdot Push (R) can be used again.",
    abilityDurationMs: "How long active Superdot abilities last.",
    pushRadius: "Reach of Superdot Push around the cursor.",
    barrierRadius: "Radius of the Superdot Barrier ring.",
    spawnEveryMs: "Milliseconds between automatic spawn waves.",
    spawnEveryPlus100Ms: "Extra spawn-interval stretch used by Roulette’s later cadence.",
    effectIntervalMs: "How often Roulette rolls a new effect.",
    enableSizeFx: "Allow Roulette effects that change cursor or ball size.",
    enableSpeedFx: "Allow Roulette effects that change speeds.",
    enableVisibilityFx: "Allow Roulette effects that hide or obscure things.",
    enableControlFx: "Allow Roulette effects that alter controls / inverted input.",
    baseCameraPxS: "Highway camera climb speed at the start of a run.",
    maxSpeedScore: "Score where Highway camera speed finishes ramping.",
    scorePerSpeedTier: "Score step between Highway speed tiers.",
    speedStepMul: "Multiply camera speed each Highway tier.",
    roadWidthFrac: "Highway road width as a fraction of screen width.",
    pointCount: "How many Darkness vision-restore points exist at once.",
    visionStartFrac: "Darkness vision radius at run start (fraction of screen).",
    visionMaxFrac: "Maximum Darkness vision radius you can rebuild to.",
    visionShrinkBasePxS: "How fast Darkness vision shrinks at low score.",
    visionShrinkMaxPxS: "How fast Darkness vision shrinks at high score.",
    visionShrinkScoreCap: "Score where Darkness shrink rate reaches its maximum.",
    appleStartCount: "Snake apples on screen when the run starts.",
    appleSpeed: "How fast Snake apples drift.",
    timeoutMs: "Combo multiplier timeout — idle this long and the combo resets.",
    roundMs: "Length of each timed round / pick cycle.",
    pickMs: "How long a timed buff/curse pick overlay stays open.",
    ballsTotal: "Total balls prepared for this mode’s pool / waves.",
    cycleMs: "Color Rush color-cycle interval.",
    respawnDelayMs: "Delay before balls respawn after being cleared.",
    heatRiseRate: "How fast Heat builds while you are near danger.",
    heatCoolRate: "How fast Heat cools when you are safe.",
    normalStart: "How many normal (non-red) balls start on screen.",
    normalPerSpawn: "Normal balls added each spawn wave.",
    redPerSpawn: "Red / special balls added each spawn wave.",
    normalSpeed: "Base speed of normal balls.",
    chaseSpeed: "How aggressively Hunter reds chase you.",
    patrolRadius: "Hunter patrol orbit radius before locking chase.",
    turnRate: "How quickly Hunter chasers turn toward you.",
    redStart: "How many red balls start on screen (Hook).",
    flySpeed: "Hook projectile flight speed toward the aim point.",
    retractMs: "How long the Hook line takes to retract.",
    wavePauseMs: "Pause between AIM waves.",
};

export function sandboxFieldHelp(_fieldPath: string, key?: string): string {
    const k = (key || _fieldPath.split(".").pop() || "").trim();
    if (!k) return "Adjusts this Sandbox value for the current run.";
    const hit = HELP_BY_KEY[k];
    if (hit) return hit;
    const label = k
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (c) => c.toUpperCase())
        .trim();
    return `Changes ${label} for this Sandbox run.`;
}
